import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Infinity as InfinityIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Loop() {
    const [signals, setSignals] = useState(null);
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [s, l] = await Promise.all([api.get("/loop/signals"), api.get("/loop/suggestions")]);
        setSignals(s.data);
        setItems(l.data);
    };
    useEffect(() => {
        load();
    }, []);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/loop/suggest");
            setItems(data.suggestions || []);
            toast.success("Suggestions refreshed");
        } catch {
            toast.error("Suggester failed");
        } finally {
            setBusy(false);
        }
    };

    if (!signals) return <LoadingBlock rows={6} />;

    return (
        <>
            <PageHeader
                eyebrow="Continuous improvement"
                title="Infinite Improvement Loop"
                subtitle="Anonymized usage signals are turned into a ranked list of next-best modules."
                actions={
                    <Button onClick={run} disabled={busy} data-testid="loop-run">
                        <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Generating…" : "Generate suggestions"}
                    </Button>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <Card className="lg:col-span-5">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><InfinityIcon className="w-4 h-4"/> Usage signals</CardTitle></CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        {Object.entries(signals).map(([k, v]) => (
                            <div key={k}>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                    {(Array.isArray(v) ? v : [String(v)]).map((x, i) => (
                                        <span key={i} className="text-xs rounded-full bg-secondary px-2 py-0.5">{x}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>

                <Card className="lg:col-span-7">
                    <CardHeader className="pb-2"><CardTitle className="text-base">Ranked next modules</CardTitle></CardHeader>
                    <CardContent>
                        {(!items || items.length === 0) ? (
                            <div className="text-sm text-muted-foreground">
                                No suggestions yet. Click "Generate suggestions" to run the loop.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="loop-suggestions">
                                {items.map((it, i) => (
                                    <div key={i} className="rounded-lg border border-border p-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="text-sm font-semibold">
                                                    <span className="text-muted-foreground tabular mr-1">{i + 1}.</span>{it.name}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">{it.why}</div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1">
                                                <StatusChip status={it.impact} label={`Impact: ${it.impact}`} />
                                                <StatusChip status={it.effort === "S" ? "clean" : it.effort === "M" ? "normal" : "high"} label={`Effort: ${it.effort}`} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
